import { CalendarCheck, Phone, Mail, MapPin, Instagram } from "lucide-react";
import Reveal from "@/components/Reveal";
import { CONTENT } from "@/content";

const Contact = () => (
  <section id="contact" className="grain bg-sage-pale py-28 md:py-36" data-testid="contact-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-12">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-gold">Prendre rendez-vous</p>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight text-anthracite md:text-6xl">
              Offrez à votre corps un moment d'<em className="text-sage-dark">écoute</em>
            </h2>
            <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-taupe">
              Les rendez-vous se prennent en ligne, en quelques instants, via
              l'agenda du cabinet. Première consultation ou suivi, chaque
              séance dure environ 60 minutes.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <a
              data-testid="contact-booking-button"
              href={CONTENT.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-shine mt-12 inline-flex items-center gap-3 rounded-md bg-sage px-10 py-5 text-sm font-medium uppercase tracking-[0.14em] text-ivory transition-colors duration-300 hover:bg-sage-dark"
            >
              <CalendarCheck className="h-5 w-5" strokeWidth={1} />
              Prendre rendez-vous
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="rounded-xl border border-sage-light/50 bg-ivory/85 p-10 backdrop-blur-sm" data-testid="contact-card">
              <p className="font-serif text-2xl font-light italic text-anthracite">Le cabinet</p>
              <div className="gold-rule mt-6" />
              <ul className="mt-8 space-y-6">
                <li className="flex items-center gap-4" data-testid="contact-address">
                  <MapPin className="h-5 w-5 shrink-0 text-sage-dark" strokeWidth={1} />
                  <span className="text-sm font-light text-taupe">{CONTENT.address}</span>
                </li>
                <li className="flex items-center gap-4" data-testid="contact-phone">
                  <Phone className="h-5 w-5 shrink-0 text-sage-dark" strokeWidth={1} />
                  <a href={`tel:${CONTENT.phone.replace(/\s/g, "")}`} className="text-sm font-light text-taupe transition-colors hover:text-anthracite">
                    {CONTENT.phone}
                  </a>
                </li>
                <li className="flex items-center gap-4" data-testid="contact-email">
                  <Mail className="h-5 w-5 shrink-0 text-sage-dark" strokeWidth={1} />
                  <a href={`mailto:${CONTENT.email}`} className="text-sm font-light text-taupe transition-colors hover:text-anthracite">
                    {CONTENT.email}
                  </a>
                </li>
                <li className="flex items-center gap-4" data-testid="contact-instagram">
                  <Instagram className="h-5 w-5 shrink-0 text-sage-dark" strokeWidth={1} />
                  <a href={CONTENT.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-light text-taupe transition-colors hover:text-anthracite">
                    Suivre le cabinet sur Instagram
                  </a>
                </li>
              </ul>
              <div className="gold-rule mt-8" />
              <div className="mt-6" data-testid="contact-hours">
                <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-taupe/70">Horaires</p>
                <ul className="mt-3 space-y-2">
                  {CONTENT.hours.map((h) => (
                    <li key={h.day} className="flex items-baseline justify-between gap-4">
                      <span className="font-serif text-base text-anthracite">{h.day}</span>
                      <span className="text-sm font-light text-taupe">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.1} className="mt-20">
        <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-taupe/70">Venir au cabinet</p>
        <div className="relative mt-6 overflow-hidden rounded-xl border border-sage-light/60" data-testid="contact-map">
          <iframe
            title="Plan d'accès au cabinet — 1321 avenue Jean Moulin, 13480 Cabriès"
            src="https://maps.google.com/maps?q=1321%20avenue%20Jean%20Moulin%2C%2013480%20Cabri%C3%A8s&z=15&ie=UTF8&iwloc=&output=embed"
            className="h-80 w-full md:h-96"
            loading="lazy"
            data-testid="contact-map-iframe"
          />
          <a
            data-testid="contact-directions-button"
            href="https://www.google.com/maps/dir/?api=1&destination=1321+avenue+Jean+Moulin,+13480+Cabri%C3%A8s"
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-5 left-5 flex items-center gap-2 rounded-md bg-ivory/95 px-6 py-3.5 text-xs font-medium uppercase tracking-[0.14em] text-anthracite backdrop-blur-sm transition-colors duration-300 hover:bg-ivory"
          >
            <MapPin className="h-4 w-4 text-sage-dark" strokeWidth={1.2} />
            Itinéraire
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default Contact;
